import type { IArticleConfig } from '~/config/article';
import { debugLog } from '~/debug';
import Session from '~/modules/Session';

// import WebVitalsReporter from './WebVitalsReporter';

/**
 * IVT Traffic Type codes
 * 1000	Valid
 * 1001	Abusive Traffic
 * 1002	Automated Traffic
 * 1003	Client Manipulation
 * 1004	Crawler
 * 1005	DataCenter
 * 1006	Hidden
 * 1007	Invalid App
 * 1008	Invalid Client
 * 1009	Invalid Device
 * 1010	Invalid Domain
 * 1011	Invalid UserAgent
 * 1012	Proxy
 * 1013	Reputation
 * 1014	Spoofed Server
 * 1015	Susceptible Device
 * 1016	User Blacklisted
 * 1017	UserAgent Tampering
 */
export type IVT =
  | 'ivt_1000'
  | 'ivt_1001'
  | 'ivt_1002'
  | 'ivt_1003'
  | 'ivt_1004'
  | 'ivt_1005'
  | 'ivt_1006'
  | 'ivt_1007'
  | 'ivt_1008'
  | 'ivt_1009'
  | 'ivt_1010'
  | 'ivt_1011'
  | 'ivt_1012'
  | 'ivt_1013'
  | 'ivt_1014'
  | 'ivt_1015'
  | 'ivt_1016'
  | 'ivt_1017';

/** 📦 Possible Logger Actions */
export type ILoggerAction =
  | 'share_facebook'
  | 'share_twitter'
  | 'share_pinterest'
  | 'share_linkedin'
  | 'share_whatsapp'
  | 'share_telegram'
  | 'share_reddit'
  | 'share_messenger'
  | 'share_pinterest'
  | 'share_flipboard'
  | 'share_email'
  | 'share_tumblr'
  | 'share_newsbreak'
  | 'login_facebook'
  | 'login_google'
  | 'login_twitter'
  | 'login_disqus'
  | 'view_page'
  | 'view_exit'
  | 'view_comments'
  | 'view_quizzly'
  | 'profile_modal'
  | 'profile_loadmore'
  | 'comments_loadmore'
  | 'comments_share_twitter'
  | 'comments_share_facebook'
  | 'comments_share_email'
  | 'comments_share_link'
  | 'notifications_readall'
  | 'notifications_seeall'
  | 'notifications_refresh'
  | 'search'
  | 'search_loadmore'
  | 'powerbar_more'
  | 'ads_false'
  | 'ads_block'
  | 'ads_block2'
  | 'policy_bad'
  | 'web_vitals'
  | 'audio_clicked'
  | 'audio_paused'
  | 'audio_resumed'
  | 'audio_restarted'
  | 'no_gdpr_support'
  | 'no_ccpa_support'
  | 'logout'
  | 'bot_detected'
  | 'ad_break'
  | IVT
  | 'exit_5s'
  | 'page_unload'
  | 'CWbubbleClick'
  | 'chat_open'
  | 'unfilled_ads'
  | 'user_register'
  | 'quizzly_view_time'
  | 'gam_detect'
  | 'login_cw'
  | 'login_qw';

/** 📦 Server Log request data structure */
export interface IRequestParams {
  /** Action name */
  action: ILoggerAction;
  /** Host / Article Domain */
  hostname: string;
  /** Ref host from config */
  refDomain: string;
  /**
   * Session ID from Session class
   * @see {@link Session.sessionID}
   */
  sessionId: string;
  /**
   * User API key
   * @see {@link Session.userAPIKey}
   */
  hashed_email: string;
  /** Host */
  pubdomain: string;
  /** API request version */
  version: string;
  /** Article url */
  hashed_article_url: string;
  /** Article ID */
  article_id: string;
  /** Article tags */
  tags: string;
  /** Article Title */
  articleTitle: string;
  /** Article Image */
  articleImg: string;
  /** Article Author */
  referrer: string;
  /** User's browser name */
  browser?: string;
  /** User's OS name */
  os?: string;
  /** User's device */
  device?: 'Mobile' | 'Tablet' | 'Desktop' | 'AMP';
  /** Is it safe to run ad campaigns on the website or not */
  isArticleBrandSafe?: boolean | null;
  /** Timestamp of when the Quiz enters user's viewport */
  ViewingStartedTs?: number;
  /** Timestamp of when the Quiz leaves user's viewport */
  ViewingEndedTs?: number;
  /** The div id of the rendered ad, used for `gam_detect` BQ event */
  adDivId?: string;
}

interface IParamaterOverride {
  key: keyof IRequestParams;
  value: IRequestParams[IParamaterOverride['key']];
}

class ServerLogger {
  /** We fire view_page in some specific scenarios, we need this class member to prevent us from firing view_page multiple times */
  public static viewPageFired = false;

  public static exit5sFired = false;

  /** Has ServerLogger sent the CWV information or not */
  // public static webVitalsReported: boolean = false;

  /** Is bot user agent detection reported */
  public static botReported: boolean = false;

  /** Callbacks to fire when IP is obtained */
  private static onIPReceived: Map<string, (value: any) => any> = new Map();

  /**
   * Add an IP listener
   * @param index
   * @param callback
   */
  public static addIPListener(index: string, callback: (value: any) => any) {
    this.onIPReceived.set(index, callback);
  }

  /**
   * Remove an IP listener by index
   * @param index the index
   */
  public static removeIPListenersByIndex(index: string) {
    if (this.onIPReceived.get('-' + index)) {
      this.onIPReceived.delete('-' + index);
    }
  }

  /**
   * Returns the BQ publish event parameters either in Object or string format
   * @param articleConfig
   * @param action
   * @param stringParams
   * @returns
   */
  private static getParams(
    articleConfig: IArticleConfig,
    action: ILoggerAction,
    stringParams: boolean = false
  ): IRequestParams | string {
    const articleTitle = ServerLogger.cleanString(articleConfig.title || '');
    const params = {
      action,
      hashed_email: Session.userAPIKey,
      hostname: articleConfig.apiKey,
      pubdomain: articleConfig.host,
      refDomain: ServerLogger.cleanString(articleConfig.refHost || ''),
      sessionId: Session.sessionID,
      version: '4.20',

      articleImg: '', // articleConfig.img temporarily sent empty
      articleTitle: encodeURI(articleTitle).replace(/#/g, '%23'),
      article_id: encodeURI(articleConfig.articleId || '').replace(/#/g, '%23'),
      hashed_article_url: encodeURIComponent(ServerLogger.cleanUrl(articleConfig.url)),
      referrer: encodeURI(articleConfig.author || ''),
      tags: ServerLogger.processTag(articleConfig.tags || ''),

      browser: Session.browser,
      device: Session.device as IRequestParams['device'],
      os: Session.os,

      isArticleBrandSafe: Session.isArticleBrandSafe,
    };

    let stringifiedParams = '';

    if (stringParams) {
      Object.keys(params).forEach((key, index) => {
        if (index === 0) {
          stringifiedParams += `${key}=${params[key as keyof typeof params]}`;
        } else {
          stringifiedParams += `&${key}=${params[key as keyof typeof params]}`;
        }
      });
    }

    return stringParams ? stringifiedParams : params;
  }

  public static async reportBot(articleConfig: IArticleConfig) {
    if (ServerLogger.botReported) {
      return;
    }

    try {
      if (articleConfig.url.trim() !== '') {
        const reqParams = ServerLogger.getParams(articleConfig, 'bot_detected');

        /** AMP events should be calculated separately */
        if (articleConfig.amp) {
          (reqParams as IRequestParams).device = 'AMP';
        }
        const xhr = new XMLHttpRequest();
        xhr.open('GET', `https://publish.vuukle.com/bq-publish?callback=&${JSON.stringify(reqParams)}&_=1489139930741`);
        xhr.send();
        ServerLogger.botReported = true;
      }
    } catch (_ex) {
      debugLog('warn', 'ServerLogger request failed');
    }
  }

  /**
   * @static
   * @description Sends log to the server based on current article config and action as a POST request. It can also send a beacon
   * @param parameterOverrides
   * @param beacon
   * @param {string} action - action to log
   * @param {object} articleConfig - current article config
   * @returns {Promise<void>}
   */
  public static async logPost(
    action: ILoggerAction,
    articleConfig: IArticleConfig,
    beacon: boolean = false,
    parameterOverrides: IParamaterOverride[] = []
  ): Promise<void> {
    debugLog(
      'log',
      `📊 [BQ Event]
      - action: ${action},
      - article params: ${JSON.stringify(articleConfig)}`
    );

    if (action === 'exit_5s' && ServerLogger.exit5sFired) {
      return;
    }

    ServerLogger.exit5sFired = true;

    try {
      if (articleConfig.url.trim() !== '') {
        let reqParams: string = ServerLogger.getParams(articleConfig, action, true) as string;

        if (parameterOverrides.length > 0) {
          parameterOverrides.forEach((parameter) => {
            reqParams += `&${parameter.key}=${parameter.value}`;
          });
        }

        /** AMP events should be calculated separately */
        if (articleConfig.amp) {
          reqParams += '&device=AMP';
        }

        if (beacon) {
          const params = ServerLogger.getParams(articleConfig, action) as IRequestParams;
          if (articleConfig.amp) {
            params.device = 'AMP';
          }
          if (parameterOverrides.length > 0) {
            parameterOverrides.forEach((parameter) => {
              (params[parameter.key] as any) = parameter.value;
            });
          }
          navigator.sendBeacon('https://publish.vuukle.com/bq-publish', JSON.stringify(params));
        } else {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', 'https://publish.vuukle.com/bq-publish');
          xhr.send(reqParams);
          if (action === 'view_page') {
            xhr.onreadystatechange = () => {
              if ((xhr.readyState === 4 || xhr.status === 200) && xhr.responseText) {
                const response = JSON.parse(xhr.response);
                ServerLogger.onIPReceived.forEach((cb) => cb(response.ip));
                if (!window._vuukleGeo) {
                  window._vuukleGeo = {
                    ip: response.ip,
                  };
                } else {
                  window._vuukleGeo.ip = response.ip;
                }
              }
            };
          }
        }
      }
    } catch (_ex) {
      debugLog('warn', 'ServerLogger request failed');
    }
  }

  /**
   * @static
   * @description Sends log to the server based on current article config and action
   * @param parameterOverride
   * @param parameterOverride.key
   * @param parameterOverride.value
   * @param parameterOverrides
   * @param {string} action - action to log
   * @param {object} articleConfig - current article config
   * @returns {Promise<void>}
   */
  public static async log(
    action: ILoggerAction,
    articleConfig: IArticleConfig,
    parameterOverrides: IParamaterOverride[] = []
  ): Promise<void> {
    /** Do not report BQ events if a bot was detected and if it's not an IVT event */
    if (Session.isBot && !action.includes('ivt')) {
      return;
    }

    /** Delay until country is determined and we know whether it's safe or not safe to run ad campaigns */
    if (
      !Session.userCountryDetermined ||
      (!Session.isArticleBrandSafeKnown && articleConfig.host === 'thephoblographer.com')
    ) {
      setTimeout(() => ServerLogger.log(action, articleConfig, parameterOverrides), 100);
      return;
    }

    // No duplicate view_page firing
    if (action === 'view_page' && ServerLogger.viewPageFired) {
      return;
    } else {
      if (action === 'view_page') {
        ServerLogger.viewPageFired = true;
      }
    }

    debugLog(
      'log',
      `📊 [BQ Event]
      - action: ${action},
      - article params: ${JSON.stringify(articleConfig)}`
    );

    try {
      if (articleConfig.url.trim() !== '') {
        const reqParams: IRequestParams = ServerLogger.getParams(articleConfig, action) as IRequestParams;

        if (parameterOverrides.length > 0) {
          parameterOverrides.forEach((parameter) => {
            (reqParams[parameter.key] as any) = parameter.value;
          });
        }

        /** AMP events should be calculated separately */
        if (articleConfig.amp) {
          reqParams.device = 'AMP';
        }

        if (action !== 'view_page') {
          delete reqParams.isArticleBrandSafe;
        }

        // const xhr = new XMLHttpRequest();

        // xhr.open(
        //   'GET',
        //   `https://vuukle-bigquery.appspot.com/bq-publish?callback=&${JSON.stringify(reqParams)}&_=1489139930741`
        // );
        // xhr.send();

        const xhr = new XMLHttpRequest();
        xhr.open('GET', `https://publish.vuukle.com/bq-publish?callback=&${JSON.stringify(reqParams)}&_=1489139930741`);
        xhr.send();
        if (action === 'view_page') {
          xhr.onreadystatechange = () => {
            if ((xhr.readyState === 4 || xhr.status === 200) && xhr.responseText) {
              const response = JSON.parse(xhr.response);
              ServerLogger.onIPReceived.forEach((cb) => cb(response.ip));
              if (!window._vuukleGeo) {
                window._vuukleGeo = {
                  ip: response.ip,
                };
              } else {
                window._vuukleGeo.ip = response.ip;
              }
            }
          };
        }
        // Send dupe request from deccanchronicle
        // if (articleConfig.apiKey === 'de0e13e8-531d-43ce-a154-0cc6451118ef') {
        //   const xhr2 = new XMLHttpRequest();
        //   xhr2.open(
        //     'GET',
        //     `https://publish.vuukle.com/bq-publish/new?callback=&${JSON.stringify(reqParams)}&_=1489139930741`
        //   );
        //   xhr2.send();
        // }
        // xhr.open('GET', `https://pub.vuukle.com/bq-publish?callback=&${JSON.stringify(reqParams)}&_=1489139930741`);
        // xhr2.send(JSON.stringify(reqParams));
      }
    } catch (_ex) {
      debugLog('warn', 'ServerLogger request failed');
    }

    // if (ServerLogger.viewPageFired && !ServerLogger.webVitalsReported) {
    //   ServerLogger.webVitalsReported = true;
    //   ServerLogger.log('web_vitals', articleConfig, [
    //     {
    //       key: 'webVitals',
    //       value: {
    //         cls: WebVitalsReporter.CLSScore,
    //         fcp: WebVitalsReporter.FCPScore,
    //         lcp: WebVitalsReporter.LCPScore,
    //       },
    //     },
    //   ]);
    // }
  }

  /**
   * [OLD] Removes symbols from a complicated string for analytics API request
   * TODO: Needs improvements
   * @private
   * @param {string} str - string in which to remove symbols
   * @returns {string}
   */
  private static cleanString(str: string): string {
    // TODO: taken from old code. Needs improvements
    let res = '';
    try {
      res = str.replace(/[\[\]\{\}\\/\"\']/g, '');
    } catch (_ne) {
      res = str
        .replace(/\[/g, '')
        .replace(/\]/g, '')
        .replace(/"/g, '')
        .replace(/'/g, '')
        .replace(/\{/g, '')
        .replace(/\}/g, '')
        .replace(/\\/g, '');
    }
    if (!res || res.length === 0) {
      res = str.replace('[', '').replace(']', '').replace(/"/g, '').replace(/'/g, '');
    }
    return res;
  }

  /**
   * This function takes a single tag, removes special symbols and returns the first 10 chars of the tag
   * @param tag Tag to process
   * @returns Processed tag
   */
  private static processTag(tag: string | string[]): string {
    const firstTag = Array.isArray(tag) ? tag[0] : tag.split(',')[0];
    const cleanedTag = firstTag.replace(/[`~!@#$%^&*()_|+\-=?;:'",.<>\{\}\[\]\\\/]/gi, '');
    return cleanedTag.slice(0, 10);
  }

  /**
   * Return the url with hostname + pathname without the http(s)://, www, m and etc.
   * @param url
   * @returns {string}
   */
  private static cleanUrl(url: string): string {
    if (url.trim() === '' || !url) {
      return '';
    }

    try {
      const urlObject = new URL(url);
      return urlObject.hostname.replace('www.', '').replace(/^(m\.)/, '') + urlObject.pathname + urlObject.search;
    } catch (_e) {
      return '';
    }
  }
}

export default ServerLogger;
